import { Message } from '@/hooks/useConversationMessages'
import { parseWhatsAppFormatting } from './messageUtils'
import { FormSubmission } from './FormSubmission'
import { OrderDetails } from './OrderDetails'
import { OrderPlaced } from './OrderPlaced'
import { ShoppingCart, List, MessageSquare } from 'lucide-react'

const BRAND_CARD_CLASS = 'max-w-xs'
const BRAND_CARD_STYLE = { fontFamily: 'var(--wn-font)' } as const
const BRAND_ICON_TILE = 'h-9 w-9 rounded-[10px] flex items-center justify-center shrink-0'
const BRAND_TITLE_STYLE = { fontFamily: 'var(--wn-font)', fontSize: 13, fontWeight: 700, color: '#101828' } as const
const BRAND_SUBTITLE_STYLE = { fontFamily: 'var(--wn-font)', fontSize: 11, fontWeight: 500, color: '#6a7282' } as const

interface UserInteractionProps {
  message: Message
}

export function UserInteraction({ message }: UserInteractionProps) {
  let interactionData = null
  
  const normalizeContentText = (content: any): string => {
    if (!content) return ''
    if (typeof content === 'string') return content
    if (typeof content === 'object') {
      return (content as any).title || (content as any).text || (content as any).id || String(content)
    }
    return String(content)
  }
  
  const normalizedContentText = normalizeContentText(message.content_text)
  let displayText = normalizedContentText

  if ((message as any).interactiveData) {
    interactionData = (message as any).interactiveData
    if (interactionData.display_text) {
      displayText = interactionData.display_text
    }
  } else {
    try {
      const contentToParse = normalizedContentText || '{}'
      const contentString = typeof contentToParse === 'string' ? contentToParse : JSON.stringify(contentToParse)
      
      let parsedContent = null
      if (contentString.trim().startsWith('{') || contentString.trim().startsWith('[')) {
        try {
          parsedContent = JSON.parse(contentString)
        } catch (parseError) {
        }
      } else {
        try {
          parsedContent = JSON.parse(contentString.trim())
        } catch (parseError) {
        }
      }
      
      if (parsedContent && typeof parsedContent === 'object') {
          if (parsedContent.interactiveData && typeof parsedContent.interactiveData === 'object') {
            interactionData = parsedContent.interactiveData
            displayText = interactionData.display_text || parsedContent.text || displayText
          }
          else if (parsedContent.interactive && typeof parsedContent.interactive === 'object') {
            interactionData = parsedContent.interactive
            const inner = parsedContent.interactive
            displayText = inner.button_reply?.title || inner.list_reply?.title || inner.display_text || displayText
          }
          else if (parsedContent.type === 'form_submission' ||
                   parsedContent.type === 'nfm_reply' ||
                   parsedContent.type === 'button_reply' ||
                   parsedContent.type === 'list_reply' ||
                   parsedContent.type === 'button_click' ||
                   parsedContent.form_data ||
                   parsedContent.response_json) {
            interactionData = parsedContent
            displayText = parsedContent.display_text || parsedContent.text || displayText
          }
          else if (parsedContent.text && !interactionData) {
            displayText = parsedContent.text
          }
          else if (parsedContent.type === 'whatsapp_flow' || parsedContent.type === 'template') {
            displayText = parsedContent.bodyText || parsedContent.text || displayText
            interactionData = parsedContent
          }
          else {
            interactionData = parsedContent
            displayText = parsedContent.text || parsedContent.title || parsedContent.id || ''
          }
      } else {
        displayText = String(contentString || 'Button clicked')
      }
    } catch (e) {
      displayText = normalizedContentText || 'Button clicked'
    }
  }
  
  if (typeof displayText !== 'string') {
    if (displayText && typeof displayText === 'object') {
      displayText = (displayText as any).title || (displayText as any).text || (displayText as any).id || ''
    } else {
      displayText = String(displayText || '')
    }
  }

  const trimmed = displayText.trim()
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    displayText = ''
  }

  if (interactionData && typeof interactionData === 'object') {
    if (interactionData.type === 'button_reply' && interactionData.button_reply) {
      const buttonText = interactionData.button_reply.title || interactionData.button_reply.id

      if (buttonText.toLowerCase().includes('cart') ||
        buttonText.toLowerCase().includes('view sent cart') ||
        buttonText.toLowerCase().includes('checkout')) {
        return (
          <div className={BRAND_CARD_CLASS} style={BRAND_CARD_STYLE}>
            <div className="flex items-center gap-2.5">
              <div className={BRAND_ICON_TILE} style={{ background: '#E8F8F0' }}>
                <ShoppingCart className="h-4 w-4" style={{ color: '#1EB677' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate" style={BRAND_TITLE_STYLE}>Cart Action</div>
                <div className="truncate mt-0.5" style={BRAND_SUBTITLE_STYLE}>Shopping cart updated</div>
              </div>
            </div>
            <div
              className="mt-2.5 pt-2.5 border-t border-[#edf1f7]"
              style={{ fontFamily: 'var(--wn-font)', fontSize: 12, fontWeight: 500, color: '#101828' }}
            >
              {buttonText}
            </div>
          </div>
        )
      }

      return (
        <div
          className="max-w-xs"
          style={{
            fontFamily: 'var(--wn-font)',
            fontSize: 13,
            fontWeight: 600,
            color: '#101828',
          }}
        >
          {buttonText}
        </div>
      )
    }

    if (interactionData.type === 'nfm_reply' && interactionData.nfm_reply) {
      let formData = interactionData.nfm_reply
      if (formData.response_json) {
        try {
          formData = JSON.parse(formData.response_json)
        } catch (e) {
          formData = formData.response_json ? { raw: formData.response_json } : formData
        }
      }
      return <FormSubmission formData={formData} />
    }

    if (interactionData.type === 'form_submission') {
      const formDataString = interactionData.form_data || interactionData.response_json
      let formData = null
      
      if (formDataString && typeof formDataString === 'string') {
        try {
          formData = JSON.parse(formDataString)
        } catch (e) {
          if (interactionData.response_json && 
              interactionData.response_json !== formDataString && 
              typeof interactionData.response_json === 'string') {
            try {
              formData = JSON.parse(interactionData.response_json)
            } catch (e2) {
              formData = { raw: formDataString }
            }
          } else {
            formData = { raw: formDataString }
          }
        }
      } else if (formDataString && typeof formDataString === 'object') {
        formData = formDataString
      }
      
      if (!formData || (typeof formData === 'object' && Object.keys(formData).length === 0)) {
        formData = { message: 'Form submitted' }
      }
      
      return <FormSubmission formData={formData} />
    }

    if (interactionData.type === 'list_reply' && interactionData.list_reply) {
      const listItem = interactionData.list_reply
      return (
        <div className={BRAND_CARD_CLASS} style={BRAND_CARD_STYLE}>
          <div className="flex items-center gap-2.5">
            <div className={BRAND_ICON_TILE} style={{ background: '#E8F8F0' }}>
              <List className="h-4 w-4" style={{ color: '#1EB677' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate" style={BRAND_TITLE_STYLE}>{listItem.title || listItem.id}</div>
              {listItem.description && (
                <div className="truncate mt-0.5" style={BRAND_SUBTITLE_STYLE}>{listItem.description}</div>
              )}
            </div>
          </div>
        </div>
      )
    }

    if (interactionData.type === 'order_placed') {
      return <OrderPlaced interactionData={interactionData} />
    }

    if (interactionData.cart || interactionData.order || interactionData.products) {
      return <OrderDetails data={interactionData} />
    }
  }

  const isListReply = interactionData?.type === 'list_reply'

  return (
    <div className={BRAND_CARD_CLASS} style={BRAND_CARD_STYLE}>
      <div className="flex items-center gap-2.5">
        <div className={BRAND_ICON_TILE} style={{ background: '#E8F8F0' }}>
          {isListReply ? (
            <List className="h-4 w-4" style={{ color: '#1EB677' }} />
          ) : (
            <MessageSquare className="h-4 w-4" style={{ color: '#1EB677' }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="truncate" style={BRAND_TITLE_STYLE}>
            {isListReply ? 'List Selection' : 'User Response'}
          </div>
          {displayText && (
            <div className="truncate mt-0.5" style={BRAND_SUBTITLE_STYLE}>
              {parseWhatsAppFormatting(displayText)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


